import { postRightworkAPI } from "../requests/utilities/requests";

// WebRTC client for VOICE roleplay conversations against the OpenAI Realtime
// API (GA endpoints). A deliberately trimmed fork of
// assistant/realtimeService.js — kept: mic acquisition, SDP exchange,
// data-channel event loop, barge-in, the echo-hallucination filter, mute,
// resource cleanup. Dropped (vs the KB assistant service): RAG function
// round-trip, interaction logging, 60-minute session timers, visibility
// reconnection, and multi-attempt reconnect — roleplay conversations run a
// few minutes, and a dropped session falls back to TEXT with the transcript
// intact (attempts sync turns server-side; preview holds them client-side).
// Added: the `end_scenario` function tool (how the persona ends the scene —
// a spoken sentinel is not an option in voice), a speak-first kick so the
// persona opens with the authored line, and a 3-minute idle auto-disconnect
// as the cost backstop.
//
// GA note: sessions are minted server-side via /v1/realtime/client_secrets
// and the SDP exchange goes to /v1/realtime/calls. Event names are handled
// for both GA (`response.output_audio*`) and legacy (`response.audio*`)
// variants defensively.

export const ConnectionState = {
  DISCONNECTED: "DISCONNECTED",
  CONNECTING: "CONNECTING",
  CONNECTED: "CONNECTED",
  ERROR: "ERROR",
};

export const ActivityState = {
  IDLE: "IDLE",
  LISTENING: "LISTENING",
  THINKING: "THINKING",
  SPEAKING: "SPEAKING",
};

const IDLE_TIMEOUT_MS = 3 * 60 * 1000; // cost backstop — see header comment
// Grace period after the scenario-ending response finishes generating, so the
// closing line's AUDIO (which lags the events slightly) can finish playing
// before the caller tears the connection down.
const SCENARIO_END_GRACE_MS = 3000;

export default class RoleplayRealtimeService {
  /**
   * @param {Object} callbacks
   * @param {function(string)} callbacks.onConnectionStateChange
   * @param {function(string)} callbacks.onActivityStateChange
   * @param {function()} callbacks.onSpeechStopped             - trainee utterance committed (VAD)
   * @param {function(string)} callbacks.onUserTranscript      - finalized trainee speech
   * @param {function(string, boolean)} callbacks.onAssistantTranscript - (text, isFinal)
   * @param {function(boolean)} callbacks.onResponseDone       - response finished (arg: scenario is ending)
   * @param {function()} callbacks.onScenarioEnd               - persona called end_scenario (post playback grace)
   * @param {function(string)} callbacks.onError
   * @param {function(string)} callbacks.onWarning
   */
  constructor(callbacks) {
    this._callbacks = callbacks;
    this._pc = null;
    this._dc = null;
    this._audioElement = null;
    this._localStream = null;

    // Barge-in tracking
    this._currentItemId = null;
    this._audioPlaybackMs = 0;
    this._audioPlaybackInterval = null;

    // Function-call accumulators keyed by item_id
    this._pendingFunctionCalls = {};
    // Set when end_scenario was called; onScenarioEnd fires on response.done
    // (after the closing line finishes generating) + a playback grace delay.
    this._scenarioEndPending = false;
    this._scenarioEndTimer = null;

    this._idleTimer = null;
    this._connectGeneration = 0;
    this._connectionState = ConnectionState.DISCONNECTED;
    this._activityState = ActivityState.IDLE;
    this._isMuted = false;
  }

  get connectionState() {
    return this._connectionState;
  }

  _setConnectionState(state) {
    if (this._connectionState === state) return; // no-op transitions don't re-notify
    this._connectionState = state;
    this._callbacks.onConnectionStateChange?.(state);
  }

  _setActivityState(state) {
    this._activityState = state;
    this._callbacks.onActivityStateChange?.(state);
  }

  /**
   * Connect: mint an ephemeral session via `sessionUrl` (attempt or preview
   * endpoint) with `sessionParams`, then open WebRTC to OpenAI.
   * @param {Object} opts
   * @param {string} opts.sessionUrl     - Rightwork mint endpoint
   * @param {Object} opts.sessionParams  - endpoint body
   * @param {boolean} opts.speakFirst    - kick a response so the persona opens
   * @param {string} [opts.openingLine]  - authored first line; when provided
   *   with speakFirst, the kick carries a per-response verbatim directive so
   *   the persona SAYS this exact line (session-level "begin by saying" is
   *   too soft — the model paraphrases it)
   */
  async connect({ sessionUrl, sessionParams, speakFirst, openingLine }) {
    this._connectGeneration++;
    const generation = this._connectGeneration;
    this._setConnectionState(ConnectionState.CONNECTING);

    let stream;
    try {
      // Mic first: fail fast before spending an ephemeral token.
      stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
    } catch (err) {
      if (this._connectGeneration !== generation) return;
      this._setConnectionState(ConnectionState.ERROR);
      this._callbacks.onError?.(
        "Microphone access is required for voice. Allow microphone access and try again."
      );
      return;
    }

    if (this._connectGeneration !== generation) {
      stream.getTracks().forEach((t) => t.stop());
      return;
    }
    this._localStream = stream;
    if (this._isMuted) {
      this._localStream.getAudioTracks().forEach((t) => {
        t.enabled = false;
      });
    }

    try {
      const sessionResp = await postRightworkAPI(sessionUrl, sessionParams);
      if (this._connectGeneration !== generation) {
        this._releaseResources();
        return;
      }
      const sessionData = sessionResp.data?.redux?.payload;
      const ephemeralKey = sessionData?.client_secret?.value;
      if (!ephemeralKey) {
        throw new Error("No ephemeral key received");
      }

      this._pc = new RTCPeerConnection();
      this._localStream.getTracks().forEach((track) => {
        this._pc.addTrack(track, this._localStream);
      });

      // Remote audio playback — synchronous in ontrack for Safari autoplay.
      this._audioElement = document.createElement("audio");
      this._audioElement.autoplay = true;
      this._pc.ontrack = (event) => {
        this._audioElement.srcObject = event.streams[0];
      };

      this._pc.oniceconnectionstatechange = () => {
        const state = this._pc?.iceConnectionState;
        if (state === "failed" || state === "disconnected") {
          // No reconnect machinery (see header): surface it; the player falls
          // back to text with the transcript intact.
          if (this._connectionState === ConnectionState.CONNECTED) {
            this._callbacks.onError?.(
              "Voice connection lost. You can keep going by typing."
            );
            this.disconnect();
          }
        }
      };

      this._dc = this._pc.createDataChannel("oai-events");
      this._dc.onopen = () => {
        if (this._connectGeneration !== generation) return;
        this._setConnectionState(ConnectionState.CONNECTED);
        this._setActivityState(ActivityState.LISTENING);
        this._resetIdleTimer();
        if (speakFirst) {
          // Fresh conversation: kick the persona's first response. With an
          // authored opening line, a per-response instruction makes the
          // delivery verbatim (near-deterministic) instead of paraphrased.
          const kick = openingLine
            ? {
                response: {
                  instructions:
                    "Say exactly this, word for word, in your character's " +
                    `natural voice, and nothing else: "${openingLine}"`,
                },
              }
            : {};
          this._sendDataChannelEvent("response.create", kick);
          this._setActivityState(ActivityState.THINKING);
        }
      };
      this._dc.onmessage = (event) => this._handleDataChannelMessage(event);
      this._dc.onclose = () => {
        if (this._connectionState === ConnectionState.CONNECTED) {
          this._callbacks.onError?.(
            "Voice connection lost. You can keep going by typing."
          );
          this.disconnect();
        }
      };

      const offer = await this._pc.createOffer();
      await this._pc.setLocalDescription(offer);
      if (this._connectGeneration !== generation) {
        this._releaseResources();
        return;
      }

      // GA WebRTC endpoint (the beta /v1/realtime?model=... is gone along
      // with the beta sessions endpoint).
      const sdpResp = await fetch(
        "https://api.openai.com/v1/realtime/calls?model=gpt-realtime-1.5",
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${ephemeralKey}`,
            "Content-Type": "application/sdp",
          },
          body: offer.sdp,
        }
      );
      if (this._connectGeneration !== generation) {
        this._releaseResources();
        return;
      }
      if (!sdpResp.ok) {
        throw new Error(`WebRTC SDP exchange failed: ${sdpResp.status}`);
      }
      const answerSdp = await sdpResp.text();
      await this._pc.setRemoteDescription({ type: "answer", sdp: answerSdp });

      this._startPlaybackTracking();
    } catch (err) {
      if (this._connectGeneration !== generation) return;
      console.error("Voice connection error:", err);
      this._releaseResources();
      this._setConnectionState(ConnectionState.ERROR);
      this._callbacks.onError?.(
        err.response?.data?.system?.message ||
          err.message ||
          "Failed to start voice"
      );
    }
  }

  disconnect() {
    this._connectGeneration++;
    // A scenario end still waiting on the response.done frame or the
    // playback grace must survive teardown: if cleanup silently cancelled
    // it, closing the modal or switching to typing during the grace would
    // strand the attempt in_progress even though the persona ended the
    // scene. Capture before _releaseResources clears the flags; fire after
    // teardown so the callback sees a fully disconnected service (its
    // reentrant stopVoice/disconnect then no-ops).
    const scenarioEndPending =
      this._scenarioEndPending || this._scenarioEndTimer !== null;
    this._releaseResources();
    this._setConnectionState(ConnectionState.DISCONNECTED);
    this._setActivityState(ActivityState.IDLE);
    if (scenarioEndPending) {
      this._callbacks.onScenarioEnd?.();
    }
  }

  setMuted(muted) {
    this._isMuted = muted;
    if (this._localStream) {
      this._localStream.getAudioTracks().forEach((t) => {
        t.enabled = !muted;
      });
    }
  }

  // ===========================================================================
  // Data channel events (GA names, with legacy fallbacks)
  // ===========================================================================

  _handleDataChannelMessage(event) {
    let msg;
    try {
      msg = JSON.parse(event.data);
    } catch {
      return;
    }
    const type = msg.type;

    switch (type) {
      case "input_audio_buffer.speech_started":
        this._handleBargeIn();
        this._setActivityState(ActivityState.LISTENING);
        this._resetIdleTimer();
        break;

      case "input_audio_buffer.speech_stopped":
        this._setActivityState(ActivityState.THINKING);
        // One committed utterance — lets the UI drop a placeholder bubble at
        // the chronologically correct spot NOW (the transcript arrives from
        // a lagging side pass, often after the persona's reply has started).
        // Fired on stopped, not started, mirroring the KB assistant: 1:1
        // with transcription items, and no dots dangling mid-sentence.
        this._callbacks.onSpeechStopped?.();
        break;

      case "conversation.item.input_audio_transcription.completed": {
        const transcript = msg.transcript || "";
        // Echo-hallucination filter (cribbed from realtimeService.js): short
        // "transcripts" while the assistant is speaking are usually the mic
        // hearing the speaker.
        if (
          transcript.trim().length < 10 &&
          this._activityState === ActivityState.SPEAKING
        ) {
          break;
        }
        if (transcript.trim()) {
          this._callbacks.onUserTranscript?.(transcript.trim());
        }
        break;
      }

      case "response.output_item.added":
        if (msg.item?.id) {
          this._currentItemId = msg.item.id;
        }
        if (msg.item?.type === "function_call") {
          this._pendingFunctionCalls[msg.item.id] = {
            name: msg.item.name || "",
            callId: msg.item.call_id || "",
            args: "",
          };
        }
        break;

      case "response.audio.delta":
      case "response.output_audio.delta":
        this._setActivityState(ActivityState.SPEAKING);
        break;

      case "response.audio_transcript.delta":
      case "response.output_audio_transcript.delta":
        this._callbacks.onAssistantTranscript?.(msg.delta || "", false);
        break;

      case "response.audio_transcript.done":
      case "response.output_audio_transcript.done":
        this._callbacks.onAssistantTranscript?.(msg.transcript || "", true);
        break;

      case "response.function_call_arguments.done": {
        const itemId = msg.item_id;
        const completed = this._pendingFunctionCalls[itemId];
        const name = completed?.name || msg.name || "";
        delete this._pendingFunctionCalls[itemId];
        if (name === "end_scenario") {
          // Acknowledge without kicking another response — the persona
          // already spoke its closing line in this same response.
          this._sendDataChannelEvent("conversation.item.create", {
            item: {
              type: "function_call_output",
              call_id: completed?.callId || msg.call_id || "",
              output: JSON.stringify({ acknowledged: true }),
            },
          });
          this._scenarioEndPending = true;
        }
        break;
      }

      case "response.done": {
        const scenarioEnding = this._scenarioEndPending;
        // Fires BEFORE the grace timer is armed: the player flushes the
        // exchange here — with the sentinel flag, so a natural ending
        // completes the attempt atomically with its final sync — and the
        // later onScenarioEnd (post-grace) only runs grading. The flag can't
        // be read at transcript-final time: within an ending response the
        // audio item finishes before the end_scenario function-call item
        // arrives.
        this._callbacks.onResponseDone?.(scenarioEnding);
        if (scenarioEnding) {
          this._scenarioEndPending = false;
          // Let the closing line's audio finish before the caller tears down.
          this._scenarioEndTimer = setTimeout(() => {
            // Clear the handle first — disconnect() treats a live handle as
            // "end still pending" and would re-fire the callback.
            this._scenarioEndTimer = null;
            this._callbacks.onScenarioEnd?.();
          }, SCENARIO_END_GRACE_MS);
        } else if (this._activityState !== ActivityState.LISTENING) {
          this._setActivityState(ActivityState.LISTENING);
        }
        this._resetIdleTimer();
        break;
      }

      case "error":
        this._callbacks.onError?.(msg.error?.message || "A voice error occurred");
        break;

      default:
        break;
    }
  }

  // ===========================================================================
  // Barge-in (cribbed from realtimeService.js)
  // ===========================================================================

  _handleBargeIn() {
    if (this._activityState === ActivityState.SPEAKING && this._currentItemId) {
      this._sendDataChannelEvent("conversation.item.truncate", {
        item_id: this._currentItemId,
        content_index: 0,
        audio_end_ms: this._audioPlaybackMs,
      });
      this._sendDataChannelEvent("response.cancel", {});
    }
    this._audioPlaybackMs = 0;
  }

  _startPlaybackTracking() {
    this._audioPlaybackMs = 0;
    this._audioPlaybackInterval = setInterval(() => {
      if (this._activityState === ActivityState.SPEAKING) {
        this._audioPlaybackMs += 100;
      }
    }, 100);
  }

  _stopPlaybackTracking() {
    if (this._audioPlaybackInterval) {
      clearInterval(this._audioPlaybackInterval);
      this._audioPlaybackInterval = null;
    }
  }

  _sendDataChannelEvent(type, data) {
    if (this._dc && this._dc.readyState === "open") {
      this._dc.send(JSON.stringify({ type, ...data }));
    }
  }

  _resetIdleTimer() {
    if (this._idleTimer) clearTimeout(this._idleTimer);
    this._idleTimer = setTimeout(() => {
      if (this._connectionState === ConnectionState.CONNECTED) {
        this._callbacks.onWarning?.(
          "Voice paused after inactivity. You can keep going by typing or restart voice."
        );
        this.disconnect();
      }
    }, IDLE_TIMEOUT_MS);
  }

  _releaseResources() {
    this._stopPlaybackTracking();
    if (this._idleTimer) {
      clearTimeout(this._idleTimer);
      this._idleTimer = null;
    }
    if (this._scenarioEndTimer) {
      clearTimeout(this._scenarioEndTimer);
      this._scenarioEndTimer = null;
    }
    if (this._localStream) {
      this._localStream.getTracks().forEach((t) => t.stop());
      this._localStream = null;
    }
    if (this._dc) {
      try {
        this._dc.close();
      } catch {}
      this._dc = null;
    }
    if (this._pc) {
      try {
        this._pc.close();
      } catch {}
      this._pc = null;
    }
    if (this._audioElement) {
      this._audioElement.srcObject = null;
      this._audioElement = null;
    }
    this._currentItemId = null;
    this._audioPlaybackMs = 0;
    this._pendingFunctionCalls = {};
    this._scenarioEndPending = false;
  }
}
